import type {
  StructuredContent,
  StructuredContentDocument,
} from '@repo/supergraph/structuredContent/types';
import {
  findAllNodesByType,
  findNodeByType,
  isStructuredContent,
} from '@repo/supergraph/structuredContent/utils';
import { getHeadingId } from '../../../TableOfContents/utils';
import { getAllChildrenStrings } from '../../utils';
import { type TableOfContentsEntry, TableOfContentsEntryType } from './types';

const headingLevelMap: Partial<Record<TableOfContentsEntryType, number>> = {
  [TableOfContentsEntryType.H2]: 2,
  [TableOfContentsEntryType.H3]: 3,
};

/**
 * Generates table of contents entries from a structured content document
 */
function generateTableOfContentsEntries(
  doc: StructuredContentDocument,
  entryFilter: TableOfContentsEntryType = TableOfContentsEntryType.H2,
): TableOfContentsEntry[] {
  const nodeToFind =
    entryFilter === TableOfContentsEntryType.TableOfContentsTarget
      ? 'TableOfContentsTarget'
      : 'ContentHeading';
  // Find all headings
  const headings = findAllNodesByType(doc, nodeToFind);
  // Filter headings based on entry filter
  const filteredHeadings = headings.filter(heading => shouldIncludeHeading(heading, entryFilter));

  // Build table of contents entries
  const tableOfContentsEntries = filteredHeadings
    .map(heading => {
      const headingChildren = Array.isArray(heading.props.children)
        ? heading.props.children[0]
        : heading.props.children;
      const textContent = getAllChildrenStrings(heading);
      const target = textContent ? getHeadingId(textContent) : '';
      if (target && textContent) {
        return {
          content: headingChildren,
          target,
          type: entryFilter,
          textContent,
        };
      }
    })
    .filter(item => !!item);

  return tableOfContentsEntries;
}

/**
 * Determines if a heading should be included based on entry filter
 */
function shouldIncludeHeading(
  heading: StructuredContentDocument,
  entryFilter: TableOfContentsEntryType,
): boolean {
  if (heading.props.excludeFromTOC) {
    return false;
  }

  if (
    entryFilter === TableOfContentsEntryType.TableOfContentsTarget &&
    heading.type === 'TableOfContentsTarget'
  ) {
    return true;
  }

  return heading.props.level === headingLevelMap[entryFilter];
}

/**
 * Builds the final document with updated table of contents
 */
function buildDocumentWithTableOfContents(
  doc: StructuredContentDocument,
  tableOfContentsItems: TableOfContentsEntry[],
): StructuredContent {
  // Recursively update TableOfContents components with updated items
  const updateTableOfContents = (node: StructuredContent): StructuredContent => {
    if (!isStructuredContent(node)) {
      return node;
    }

    // Replace TableOfContents node with updated items
    if (node?.type === 'TableOfContents') {
      return {
        ...node,
        props: {
          ...node.props,
          items: tableOfContentsItems,
        },
      };
    }

    // Recursively process children
    if (node?.props?.children) {
      const nestedChildren = Array.isArray(node.props.children)
        ? node.props.children
        : [node.props.children];

      const updatedNestedChildren = nestedChildren.map(updateTableOfContents);

      return {
        ...node,
        props: {
          ...node.props,
          children: updatedNestedChildren,
        },
      };
    }

    return node;
  };

  const { children } = doc.props;

  const childrenArray = Array.isArray(children) ? children : [children];

  const updatedChildren = childrenArray.map(updateTableOfContents);

  return {
    ...doc,
    props: {
      ...doc.props,
      children: updatedChildren,
    },
  };
}

/**
 * Iterates over a structured content document to populate table of contents items
 */
export function populateTableOfContents(
  doc: StructuredContentDocument | undefined,
): StructuredContent {
  if (!doc || doc.type !== 'Document' || !Array.isArray(doc.props.children)) {
    return doc;
  }

  // Find TableOfContents component
  const tableOfContents = findNodeByType(doc, 'TableOfContents');
  const autoPopulateItems = tableOfContents?.props?.autoPopulateItems ?? true;

  // Return doc if no TableOfContents or auto populate is disabled
  if (!tableOfContents || !isStructuredContent(tableOfContents) || !autoPopulateItems) {
    return doc;
  }

  const entryFilter = tableOfContents.props.entryFilter;
  // Iterate over the document to find and filter headings
  const tableOfContentsItems = generateTableOfContentsEntries(doc, entryFilter);

  // Do not update doc if there are no items to populate
  if (tableOfContentsItems.length === 0) {
    return doc;
  }

  // Build the final document with updated table of contents
  return buildDocumentWithTableOfContents(doc, tableOfContentsItems);
}
